import { Box, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";

import type { TaskStatusChartRow } from "../../utils/crmOperationalDashboard.aggregate";

export type TaskStatusDistributionChartProps = {
  data: TaskStatusChartRow[];
  loading?: boolean;
};

export const TaskStatusDistributionChart = ({ data, loading }: TaskStatusDistributionChartProps) => {
  const theme = useTheme();

  const colorByKey = useMemo(
    () => ({
      primary: theme.palette.primary.main,
      warning: theme.palette.warning.main,
      success: theme.palette.success.main,
      error: theme.palette.error.main,
    }),
    [theme],
  );

  const chartData = data?.filter((d) => d.value > 0) ?? [];
  const hasData = chartData.length > 0;

  return (
    <Box sx={{ height: 280 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Task status distribution
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Todo, in progress, completed, and overdue (incomplete past due).
      </Typography>
      {loading ? (
        <Box sx={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : !hasData ? (
        <Box sx={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No tasks in scope
          </Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={88}
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colorByKey[entry.colorKey as keyof typeof colorByKey] ?? theme.palette.grey[400]}
                />
              ))}
            </Pie>
            <RechartsTooltip formatter={(value: number, name: string) => [`${value}`, name]} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};
